import React, { useEffect, useState } from "react";
import { ArrowRight, Eye, EyeOff, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdminAuthFrame } from "@/components/AdminAuthFrame";
import { supabase } from "@/lib/supabase";

const AdminResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setHasSession(Boolean(data.session)));
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) setHasSession(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 8) return toast.error("Your password must contain at least 8 characters.");
    if (password !== confirmPassword) return toast.error("The passwords do not match.");
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Password updated. You can now sign in.");
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <AdminAuthFrame eyebrow="Secure Password Reset" title="Choose a New Password" description="Create a strong password for your admin account.">
      {hasSession === false ? (
        <div className="text-center">
          <p className="text-sm text-slate-500">This reset link is invalid or has expired. Request a new link to continue.</p>
          <Link to="/admin/forgot-password" className="btn-blue mt-6 w-full">Request New Link <ArrowRight className="h-4 w-4" /></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <PasswordField label="New Password" value={password} onChange={setPassword} visible={showPassword} onToggle={() => setShowPassword((value) => !value)} testId="reset-password-input" />
          <PasswordField label="Confirm New Password" value={confirmPassword} onChange={setConfirmPassword} visible={showPassword} onToggle={() => setShowPassword((value) => !value)} testId="reset-password-confirm-input" />
          <button type="submit" disabled={submitting || hasSession === null} className="btn-blue w-full" data-testid="reset-password-submit-button">{submitting ? "Updating..." : "Update Password"} <ArrowRight className="h-4 w-4" /></button>
        </form>
      )}
    </AdminAuthFrame>
  );
};

const PasswordField: React.FC<{ label: string; value: string; onChange: (value: string) => void; visible: boolean; onToggle: () => void; testId: string }> = ({ label, value, onChange, visible, onToggle, testId }) => (
  <label className="block text-xs font-semibold text-slate-600">
    {label}
    <span className="relative mt-1.5 block">
      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input required minLength={8} type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} placeholder="At least 8 characters" autoComplete="new-password" className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-10 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid={testId} />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={visible ? "Hide password" : "Show password"}>{visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </span>
  </label>
);

export default AdminResetPassword;
